import { TEST_IDS } from '@va/constants';
import { ChartContext, ChartTooltipMode } from '@va/types/charts';
import { TimeInterval } from '@va/types/time-series';
import { Tooltip, TooltipContent, useTooltipContext } from '@va/ui/tooltips';
import { getParameterByName, removeUrlParameter, sleep } from '@va/util/helpers';
import { useOutsideClick, useWindowDimensions } from '@va/util/hooks';
import { Chart, ChartDataset, ChartType, TooltipItem } from 'chart.js';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { ChartTooltipComponent, ChartTooltipValues } from './ChartTooltipComponent';

import './external-tooltip.scss';

export type IndicatorLinesArgs = {
  chart: Chart;
  currentTimeIntervals: TimeInterval[];
  offsetLeft: number;
  lastDataIndex: number;
};

export type TooltipHeaderArgs = {
  chart: Chart;
  currentTimeIntervals: TimeInterval[];
  dataPoints: TooltipItem<ChartType>[] | undefined;
  lastDataIndex: number;
};

type ExternalChartTooltipProps = {
  tooltipMode?: ChartTooltipMode;
  reportBlockId?: string;
  showPrevious: boolean;
  title?: string;
  subtitle?: string;
  reversePercentageColors?: boolean;
  currentTimeIntervals: TimeInterval[];
  previousTimeIntervals: TimeInterval[];
  valueTransformer?: (val: any) => any;
  zIndex?: number;
  /** Renders persistent vertical indicator lines on the chart (e.g. live, limit reached). */
  renderIndicatorLines?: (args: IndicatorLinesArgs) => ReactNode;
  /** Renders a custom tooltip header chip. When it returns a non-null node, the title is suppressed. */
  renderTooltipHeader?: (args: TooltipHeaderArgs) => ReactNode;
  showTime?: boolean;
};

const CHART_TOOLTIP_CONTAINER_ID = 'chart-tooltip-container';

export const externalChartTooltip = (context: ChartContext, props: ExternalChartTooltipProps) => {
  return <ExternalChartTooltip context={context} {...props} />;
};

type TooltipProps = {
  context: ChartContext;
} & ExternalChartTooltipProps;

/**
 * Used when the chart has DATES on the 0x axis
 * Can display data for the previous period
 *
 * For other value types on the 0x axis,
 * @see BasicExternalChartTooltip
 */
export const ExternalChartTooltip = ({
  context,
  showPrevious,
  title,
  subtitle,
  reversePercentageColors,
  currentTimeIntervals,
  previousTimeIntervals,
  valueTransformer,
  tooltipMode = 'fixed',
  reportBlockId,
  zIndex = 1700,
  renderIndicatorLines,
  renderTooltipHeader,
  showTime,
}: TooltipProps) => {
  const { isMobile } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(!!context.tooltip.opacity);
  const [isTooltipInteractive, setTooltipInteractive] = useState(false);
  const { tooltip, chart } = context;
  const { height, top } = chart.chartArea;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const isChartDataAvailable = useCallback(async () => {
    const maxRetry = 20;
    const delayMS = 100;
    let itr = 0;

    while (itr <= maxRetry) {
      if (!chart || !chart.data.datasets || chart.data.datasets.length === 0) {
        await sleep(delayMS);
        itr++;
      } else {
        return true;
      }
    }

    if (!chart || !chart.data.datasets || chart.data.datasets.length === 0) return false;
    return true;
  }, [chart]);

  const enableTooltipInteractivity = useCallback(() => {
    if (chart.config.options) {
      chart.config.options.events = ['click'];
    }
    chart.update('none');
    setTooltipInteractive(true);
  }, [chart]);

  const disableTooltipInteractivity = useCallback(() => {
    if (chart.config.options) {
      chart.config.options.events = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'];
    }
    chart.update('none');
    setTooltipInteractive(false);
  }, [chart]);

  const openHighlightedTooltip = useCallback(async () => {
    const highlighted_data_index_str = getParameterByName('highlighted_data_index');
    if (!highlighted_data_index_str) return;
    const targetedReportBlockId = getParameterByName('report_block');
    if (targetedReportBlockId === '') return;
    if (targetedReportBlockId !== reportBlockId) return;

    const updatedUrl = removeUrlParameter(['highlighted_data_index', 'report_block']);
    history.replace(updatedUrl);

    const highlighted_data_index = parseInt(highlighted_data_index_str);
    if (Number.isNaN(highlighted_data_index)) return;

    const ok = await isChartDataAvailable();
    if (!ok) {
      return;
    }

    const maxLength = chart.data.datasets[0].data.length;
    if (maxLength <= highlighted_data_index) return;

    const elements = chart.data.datasets.map((_, datasetIndex) => ({
      datasetIndex,
      index: highlighted_data_index,
    }));
    chart.tooltip?.setActiveElements(elements, {
      x: 0,
      y: 0,
    });

    enableTooltipInteractivity();
  }, [chart.data.datasets, chart.tooltip, enableTooltipInteractivity, history, isChartDataAvailable, reportBlockId]);

  useEffect(() => {
    openHighlightedTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseMove = useCallback(() => {
    if (isTooltipInteractive || isOpen === Boolean(tooltip.opacity)) return;

    setIsOpen(Boolean(tooltip.opacity));
  }, [isOpen, isTooltipInteractive, tooltip.opacity]);

  const onTouchMove = useCallback(() => {
    if (!isOpen) return;
    if (isOpen === Boolean(tooltip.opacity) && !isTooltipInteractive) return;

    setIsOpen(Boolean(tooltip.opacity));
    if (isTooltipInteractive) disableTooltipInteractivity();
  }, [disableTooltipInteractivity, isOpen, isTooltipInteractive, tooltip.opacity]);

  const onTouchEnd = useCallback(() => {
    if (isOpen && !isTooltipInteractive) {
      setTooltipInteractive(true);
    }
  }, [isOpen, isTooltipInteractive]);

  const closeTooltip = useCallback(() => {
    // Clear the chart's active datapoint and restore default events to prevent tooltip from opening on any element click
    chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
    tooltip.opacity = 0;
    setIsOpen(false);
    disableTooltipInteractivity();
  }, [chart.tooltip, tooltip, disableTooltipInteractivity]);

  const handleOnScroll = useCallback(
    (e: Event) => {
      // @ts-ignore VISA-13473 - Prevent tooltip from closing when scrolling inside the tooltip
      if (e.target?.id === CHART_TOOLTIP_CONTAINER_ID) return;
      if (!isOpen) return;
      disableTooltipInteractivity();
      tooltip.opacity = 0;
      setIsOpen(false);
    },
    [disableTooltipInteractivity, isOpen, tooltip],
  );

  const debouncedOnScroll = useDebouncedCallback((e: Event) => {
    handleOnScroll(e);
  }, 250);

  const handleCanvasClick = useCallback(
    (e: MouseEvent) => {
      // Compare the DOM node (not the id string): an empty/absent id matches any element with no id.
      if (isTooltipInteractive || tooltip.opacity === 0 || e.target !== chart.canvas) return;
      setIsOpen(true);
      enableTooltipInteractivity();
    },
    [chart.canvas, enableTooltipInteractivity, isTooltipInteractive, tooltip.opacity],
  );

  useOutsideClick(tooltipRef, (e) => {
    // @ts-ignore
    if (!isTooltipInteractive || !isOpen || e.target?.id === chart.canvas.id) return;

    disableTooltipInteractivity();
  });

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);

    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchmove', onTouchMove);

    window.addEventListener('scroll', debouncedOnScroll, { capture: true });
    window.addEventListener('click', handleCanvasClick, { capture: true });

    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', debouncedOnScroll);
      window.removeEventListener('click', handleCanvasClick);
    };
  }, [debouncedOnScroll, handleCanvasClick, onMouseMove, onTouchEnd, onTouchMove]);

  const { offsetLeft } = chart.canvas;

  const placeholderSize = 2;
  const left = offsetLeft + tooltip.caretX - placeholderSize / 2;

  const { dataPoints } = tooltip;

  const lastDataIndex = (chart.data.datasets[0]?.data?.length ?? 0) - 1;

  const currentValues = useMemo(() => {
    if (!dataPoints) return {};
    return dataPoints.reduce((acc, point) => {
      const { dataset, raw, dataIndex } = point;
      if (!dataset.isCurrent) return acc;
      acc[dataset.label ?? ''] = {
        color: extractDatasetColor(dataset),
        value: raw as number,
        linkTo: dataset.linkToFunc?.({ dataIndex: dataIndex, datasetKey: dataset.id ?? '' }),
        // @ts-ignore
        onLinkClick: dataset.onLinkClick,
        // @ts-ignore
        linkLabel: dataset.linkLabel,
        // @ts-ignore
        labelExtras: dataset?.labelExtras,
      };
      return acc;
    }, {} as ChartTooltipValues);
  }, [dataPoints]);

  const previousValues = useMemo(() => {
    if (!dataPoints) return {};
    return dataPoints.reduce((acc, point) => {
      const { dataset, raw } = point;
      if (dataset.isCurrent) return acc;
      acc[dataset.label ?? ''] = { color: extractDatasetColor(dataset), value: raw as number };
      return acc;
    }, {} as ChartTooltipValues);
  }, [dataPoints]);

  if (!dataPoints && !renderIndicatorLines) return null;

  const currentDate = currentTimeIntervals[dataPoints?.[0]?.dataIndex ?? -1] ?? 'N/A';
  const previousDate = previousTimeIntervals[dataPoints?.[0]?.dataIndex ?? -1] ?? 'N/A';

  const indicatorLinesArgs: IndicatorLinesArgs = {
    chart,
    currentTimeIntervals,
    offsetLeft,
    lastDataIndex,
  };
  const tooltipHeaderArgs: TooltipHeaderArgs = {
    chart,
    currentTimeIntervals,
    dataPoints: dataPoints as any,
    lastDataIndex,
  };
  const headerContent = renderTooltipHeader?.(tooltipHeaderArgs);

  return (
    <div className='chart-tooltip-wrapper'>
      {renderIndicatorLines?.(indicatorLinesArgs)}
      <Tooltip placement={isMobile ? 'top' : undefined} interactive open={isOpen}>
        {tooltipMode === 'fixed' && (
          <FixedTrigger isVisible={isOpen} left={left} top={top} placeholderSize={placeholderSize} height={height} />
        )}
        {tooltipMode === 'follow-point' && (
          <PointFollowerTrigger isVisible={isOpen} left={tooltip.caretX} top={tooltip.caretY} />
        )}
        <TooltipContent
          tooltipClassNames={classNames('!rounded-xl p-4 !max-w-[33.75rem]', {
            'pointer-events-auto': isTooltipInteractive,
            'pointer-events-none': !isTooltipInteractive,
          })}
          zIndex={zIndex}
          containerId={CHART_TOOLTIP_CONTAINER_ID}
          ref={tooltipRef}
        >
          <ChartTooltipComponent
            currentValues={currentValues}
            previousValues={previousValues}
            currentPeriod={currentDate}
            previousPeriod={previousDate}
            title={headerContent ? undefined : title}
            subtitle={subtitle}
            showPrevious={showPrevious}
            reversePercentageColors={reversePercentageColors}
            closeTooltip={closeTooltip}
            valueTransformer={valueTransformer}
            headerContent={headerContent}
            showTime={showTime}
          />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

type CommonTooltipTriggerProps = {
  isVisible: boolean;
  top: number;
  left: number;
};

type TooltipTriggerProps = CommonTooltipTriggerProps & {
  height?: number;
  placeholderSize?: number;
  className?: string;
  bottom?: number;
};

const FixedTrigger = ({ left, top, isVisible, height, placeholderSize }: TooltipTriggerProps) => {
  return (
    <TooltipTrigger
      left={left}
      isVisible={isVisible}
      top={top}
      className='chart-datapoint'
      bottom={0}
      height={height}
      placeholderSize={placeholderSize}
    />
  );
};
const PointFollowerTrigger = ({ left, top, isVisible }: CommonTooltipTriggerProps) => {
  return <TooltipTrigger left={left} isVisible={isVisible} top={top} />;
};

const TooltipTrigger = ({ isVisible, top, left, placeholderSize, height, className, bottom }: TooltipTriggerProps) => {
  const { refs, context } = useTooltipContext();

  useEffect(() => {
    context.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, left, height]);

  // Overlaps the datapoint in the graph
  return (
    <div
      data-testid={TEST_IDS.generic.chart.datapoint}
      ref={refs.setReference}
      className={classNames(
        'absolute',
        {
          invisible: !isVisible,
        },
        className,
      )}
      style={{
        bottom: bottom,
        top: top,
        left: left,
        width: placeholderSize,
        maxHeight: height,
      }}
    />
  );
};

function extractDatasetColor(dataset: ChartDataset): string {
  if (dataset.baseColor) {
    return dataset.baseColor;
  }

  if (dataset.backgroundColor) {
    return dataset.backgroundColor as string;
  }

  return '#1E1E1E';
}
